import { useQuery } from "@tanstack/react-query";
import {
  CALL_AUDIO_RECORDINGS_PAGE_SIZES,
  type CallAudioHandlerRole,
  type CallAudioHandlerRoleKey,
  type CallAudioPageSize,
  type CallAudioPlayback,
  type CallAudioRecording,
  type CallAudioRecordingsResponse,
  type CallAudioSortColumn,
  type CallAudioSuggestionField,
} from "@t3tools/contracts";
import {
  ArrowLeftRightIcon,
  AudioLinesIcon,
  Building2Icon,
  CalendarRangeIcon,
  CaptionsIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  DownloadIcon,
  HashIcon,
  ListFilterIcon,
  MicIcon,
  PhoneIcon,
  RefreshCwIcon,
  ShieldIcon,
  TagIcon,
  TimerIcon,
  UserRoundIcon,
  UsersIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "~/components/ui/dialog";
import { Popover, PopoverPopup, PopoverTrigger } from "~/components/ui/popover";
import {
  DashboardSelect,
  DashboardsPageFrame,
  DataTable,
  KpiCard,
  KpiGrid,
  dashboardControlClassName,
} from "./dashboardKit";
import {
  callAudioRecordingsQueryOptions,
  callAudioRecordingsCountQueryOptions,
  callAudioDownloadPreviewQueryOptions,
  callAudioSuggestionsQueryOptions,
  callAudioTranscriptQueryOptions,
  cycleCallAudioSort,
  fetchCallAudioDownloadManifest,
  fetchCallAudioPlayback,
  type CallAudioPageRequest,
  type CallAudioRecordingFilters,
} from "./callAudioReactQuery";
import {
  callAudioDownloadPickerCancelled,
  chooseCallAudioDownloadDirectory,
  writeCallAudioDownload,
  type CallAudioDownloadProgress,
} from "./callAudioFolderDownload";

const CARESPCE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SORT_COLUMNS = [
  "occurredAt",
  "durationSeconds",
  "source",
  "navigatorName",
  "callType",
  "direction",
] as const satisfies readonly CallAudioSortColumn[];
const buttonClass =
  "inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-accent disabled:opacity-40";

type FilterId =
  | "callDirection"
  | "callType"
  | "caregiver"
  | "carespace"
  | "clinic"
  | "dates"
  | "duration"
  | "externalNumber"
  | "handlerRole"
  | "navigator"
  | "recordingType"
  | "source"
  | "transcript";

const HANDLER_ROLE_OPTIONS = [
  { label: "Navigator", value: "care_navigator" },
  { label: "Enrollment", value: "enrollment_specialist" },
  { label: "Admin", value: "admin" },
  { label: "Super admin", value: "super_admin" },
  { label: "No role", value: "member" },
] as const satisfies ReadonlyArray<{ label: CallAudioHandlerRole; value: CallAudioHandlerRoleKey }>;

const FILTERS: Array<{ icon: typeof CalendarRangeIcon; id: FilterId; label: string }> = [
  { icon: CalendarRangeIcon, id: "dates", label: "Date range" },
  { icon: TimerIcon, id: "duration", label: "Duration" },
  { icon: UserRoundIcon, id: "navigator", label: "Handler" },
  { icon: ShieldIcon, id: "handlerRole", label: "Handler role" },
  { icon: HashIcon, id: "carespace", label: "Carespace" },
  { icon: Building2Icon, id: "clinic", label: "Clinic" },
  { icon: UsersIcon, id: "caregiver", label: "People" },
  { icon: PhoneIcon, id: "externalNumber", label: "External number" },
  { icon: TagIcon, id: "callType", label: "Call type" },
  { icon: AudioLinesIcon, id: "source", label: "Source" },
  { icon: ArrowLeftRightIcon, id: "callDirection", label: "Direction" },
  { icon: CaptionsIcon, id: "transcript", label: "Transcript" },
  { icon: MicIcon, id: "recordingType", label: "Recording type" },
];

function etDate(offsetDays: number): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "America/New_York",
    year: "numeric",
  }).formatToParts(new Date());
  const year = Number(parts.find((part) => part.type === "year")?.value);
  const month = Number(parts.find((part) => part.type === "month")?.value);
  const day = Number(parts.find((part) => part.type === "day")?.value);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + offsetDays);
  return date.toISOString().slice(0, 10);
}

function defaultFilters(): CallAudioRecordingFilters {
  return {
    callDirections: [],
    callTypes: [],
    caregivers: [],
    carespaceIds: [],
    clinics: [],
    end: etDate(0),
    externalNumbers: [],
    handlerRoles: [],
    navigators: [],
    pageSize: 50,
    recordingTypes: [],
    sources: [],
    start: etDate(-6),
    transcripts: [],
  };
}

function rangeError(start: string, end: string): string | null {
  if (!start && !end) return null;
  if (!start || !end) return "Choose both a start and an end date, or clear both.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end) || start > end) {
    return "Choose a start date on or before the end date.";
  }
  return null;
}

function dateInRange(day: string, start: string, end: string): boolean {
  return start !== "" && end !== "" && day >= start && day <= end;
}

function formatWhen(value: string, timezone: string) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: timezone,
  }).format(date);
}

function formatDuration(seconds: number | null) {
  if (seconds == null) return "—";
  const total = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return minutes > 0 ? `${minutes}m ${rest}s` : `${rest}s`;
}

function isSortColumn(column: string): column is CallAudioSortColumn {
  return (SORT_COLUMNS as readonly string[]).includes(column);
}

function toggleValue<T extends string>(values: readonly T[], value: T): T[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

function RecordingPlayer(props: { row: CallAudioRecording }) {
  const [playback, setPlayback] = useState<CallAudioPlayback | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [started, setStarted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const remintAttempted = useRef(false);
  const { row } = props;

  function prepare(fromUser: boolean) {
    if (fromUser) remintAttempted.current = false;
    setPending(true);
    setError(null);
    setStarted(false);
    void fetchCallAudioPlayback(row.source, row.recordingId)
      .then((next) => {
        setPlayback(next);
        setExpiresAt(Date.now() + next.expiresInSeconds * 1000);
      })
      .catch((reason: unknown) => {
        setPlayback(null);
        setError(reason instanceof Error ? reason.message : "Failed to prepare call audio");
      })
      .finally(() => setPending(false));
  }

  useEffect(() => {
    if (!playback || started || expiresAt == null) return;
    const delay = Math.max(0, expiresAt - Date.now());
    const timer = setTimeout(() => {
      setPlayback(null);
      setError("This playback link expired. Play again to refresh it.");
    }, delay);
    return () => clearTimeout(timer);
  }, [expiresAt, playback, started]);

  if (playback) {
    return (
      <div className="grid justify-items-start gap-1">
        {playback.tracks.map((track, index) => (
          <audio
            className="h-8 max-w-64"
            controls
            key={track.url}
            onError={() => {
              if (!remintAttempted.current) {
                remintAttempted.current = true;
                prepare(false);
                return;
              }
              setPlayback(null);
              setError("Playback failed. Play again to request a fresh link.");
            }}
            onPlay={() => setStarted(true)}
            preload="none"
            src={track.url}
          >
            {playback.tracks.length > 1 ? `Track ${index + 1}` : "Recording"}
          </audio>
        ))}
        <button
          className={buttonClass}
          disabled={pending}
          onClick={() => prepare(true)}
          type="button"
        >
          {pending ? "Preparing…" : "Refresh link"}
        </button>
      </div>
    );
  }
  return (
    <div className="grid justify-items-start gap-1">
      <button
        className={buttonClass}
        disabled={pending}
        onClick={() => prepare(true)}
        type="button"
      >
        {pending ? "Preparing…" : "Play"}
      </button>
      {error ? <span className="max-w-56 text-xs text-red-700">{error}</span> : null}
    </div>
  );
}

function monthCells(cursor: string): string[] {
  const [yearText, monthText] = cursor.split("-");
  const year = Number(yearText);
  const month = Number(monthText) - 1;
  const count = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const lead = new Date(Date.UTC(year, month, 1)).getUTCDay();
  const cells = Array.from({ length: lead }, () => "");
  for (let day = 1; day <= count; day += 1) {
    cells.push(new Date(Date.UTC(year, month, day)).toISOString().slice(0, 10));
  }
  return cells;
}

function shiftMonth(cursor: string, offset: number): string {
  const [yearText, monthText] = cursor.split("-");
  const date = new Date(Date.UTC(Number(yearText), Number(monthText) - 1 + offset, 1));
  return date.toISOString().slice(0, 7);
}

function DateRangePicker(props: {
  end: string;
  onChange: (start: string, end: string) => void;
  start: string;
}) {
  const [cursor, setCursor] = useState((props.start || etDate(0)).slice(0, 7));
  const [anchor, setAnchor] = useState<string | null>(null);
  const error = rangeError(props.start, props.end);
  return (
    <div className="grid gap-2" data-slot="calendar">
      <div className="flex items-center justify-between gap-2">
        <button
          className={buttonClass}
          onClick={() => setCursor(shiftMonth(cursor, -1))}
          type="button"
        >
          <ChevronLeftIcon aria-hidden className="size-3.5" />
          <span className="sr-only">Previous month</span>
        </button>
        <p className="text-xs font-medium text-foreground">
          {new Intl.DateTimeFormat("en-US", {
            month: "long",
            timeZone: "UTC",
            year: "numeric",
          }).format(new Date(`${cursor}-01T00:00:00Z`))}
        </p>
        <button
          className={buttonClass}
          onClick={() => setCursor(shiftMonth(cursor, 1))}
          type="button"
        >
          <ChevronRightIcon aria-hidden className="size-3.5" />
          <span className="sr-only">Next month</span>
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-muted-foreground">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((label) => (
          <span key={label}>{label}</span>
        ))}
        {monthCells(cursor).map((day, index) =>
          day ? (
            <button
              aria-pressed={dateInRange(day, props.start, props.end)}
              className={
                dateInRange(day, props.start, props.end)
                  ? "rounded-md bg-foreground px-1 py-1 text-background"
                  : "rounded-md px-1 py-1 hover:bg-accent"
              }
              key={day}
              onClick={() => {
                if (!anchor) {
                  setAnchor(day);
                  props.onChange(day, day);
                  return;
                }
                const start = anchor < day ? anchor : day;
                const end = anchor < day ? day : anchor;
                setAnchor(null);
                props.onChange(start, end);
              }}
              type="button"
            >
              {Number(day.slice(8))}
            </button>
          ) : (
            <span key={`empty-${index}`} />
          ),
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {props.start && props.end
          ? `${props.start} to ${props.end}. Dates use America/New_York.`
          : "All dates. Dates use America/New_York."}
      </p>
      <button
        className={`${buttonClass} w-fit`}
        onClick={() => props.onChange("", "")}
        type="button"
      >
        Clear dates
      </button>
      {error ? <p className="text-xs text-red-700">{error}</p> : null}
    </div>
  );
}

function ChoiceList(props: {
  onToggle: (value: string) => void;
  options: Array<{ label: string; value: string }>;
  selected: readonly string[];
}) {
  return (
    <div className="grid gap-1">
      {props.options.map((option) => {
        const active = props.selected.includes(option.value);
        return (
          <button
            aria-pressed={active}
            className={active ? `${buttonClass} border-foreground` : buttonClass}
            key={option.value}
            onClick={() => props.onToggle(option.value)}
            type="button"
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function SuggestionList(props: {
  acceptsTypedUuid?: boolean;
  end: string;
  field: CallAudioSuggestionField;
  label: string;
  onToggle: (value: string) => void;
  selected: readonly string[];
  start: string;
}) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [typedError, setTypedError] = useState<string | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(q), 200);
    return () => clearTimeout(timer);
  }, [q]);
  const suggestions = useQuery(
    callAudioSuggestionsQueryOptions({
      enabled: true,
      end: props.end,
      field: props.field,
      q: debounced,
      start: props.start,
    }),
  );
  const values = suggestions.data?.values ?? [];
  const options = [
    ...props.selected
      .filter((value) => !values.includes(value))
      .map((value) => ({ label: value, value })),
    ...values.map((value) => ({ label: value, value })),
  ];
  return (
    <div className="grid w-56 gap-2">
      <label className="grid gap-1 text-xs text-muted-foreground">
        Search {props.label}
        <input
          className={dashboardControlClassName}
          onChange={(event) => {
            setQ(event.target.value);
            setTypedError(null);
          }}
          onKeyDown={(event) => {
            if (!props.acceptsTypedUuid || event.key !== "Enter") return;
            event.preventDefault();
            const next = q.trim();
            if (!next) return;
            if (!CARESPCE_UUID.test(next)) {
              setTypedError("Carespace id must be a UUID.");
              return;
            }
            setTypedError(null);
            props.onToggle(next);
            setQ("");
          }}
          placeholder={props.acceptsTypedUuid ? "Search or paste a UUID" : "Type to search"}
          value={q}
        />
      </label>
      {typedError ? <p className="text-xs text-red-700">{typedError}</p> : null}
      {suggestions.isLoading ? <p className="text-xs text-muted-foreground">Searching…</p> : null}
      {suggestions.error ? (
        <p className="text-xs text-red-700">Suggestions are unavailable.</p>
      ) : null}
      <ChoiceList onToggle={props.onToggle} options={options} selected={props.selected} />
      {props.selected.length > 0 ? (
        <p className="text-xs text-muted-foreground">{props.selected.length} selected</p>
      ) : null}
    </div>
  );
}

function ExternalNumberFilter(props: { onToggle: (value: string) => void; selected: string[] }) {
  const [value, setValue] = useState("");
  return (
    <div className="grid w-56 gap-2">
      <label className="grid gap-1 text-xs text-muted-foreground">
        Exact external number
        <input
          className={dashboardControlClassName}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            const next = value.trim();
            if (!next) return;
            props.onToggle(next);
            setValue("");
          }}
          placeholder="Add and press Enter"
          value={value}
        />
      </label>
      <ChoiceList
        onToggle={props.onToggle}
        options={props.selected.map((number) => ({ label: number, value: number }))}
        selected={props.selected}
      />
    </div>
  );
}

function FilterMenu(props: {
  filters: CallAudioRecordingFilters;
  onChange: (filters: CallAudioRecordingFilters) => void;
}) {
  const [active, setActive] = useState<FilterId>("dates");
  const { filters } = props;
  const move = (offset: number) => {
    const index = FILTERS.findIndex((item) => item.id === active);
    const next = FILTERS[(index + offset + FILTERS.length) % FILTERS.length];
    if (next) setActive(next.id);
  };
  return (
    <Popover>
      <PopoverTrigger className={buttonClass}>
        <ListFilterIcon aria-hidden className="size-3.5" />
        Filters
      </PopoverTrigger>
      <PopoverPopup align="start" className="w-auto max-w-[40rem] p-0">
        <div className="flex max-h-96 min-h-72">
          <div
            aria-label="Recording filters"
            className="grid w-48 content-start gap-1 border-r border-border/70 p-2"
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                move(1);
              }
              if (event.key === "ArrowUp") {
                event.preventDefault();
                move(-1);
              }
            }}
            role="menu"
          >
            {FILTERS.map((item) => {
              const Icon = item.icon;
              const selected = item.id === active;
              return (
                <button
                  aria-expanded={selected}
                  className={
                    selected
                      ? "flex items-center gap-2 rounded-md bg-accent px-2 py-1.5 text-left text-xs font-medium"
                      : "flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-accent"
                  }
                  key={item.id}
                  onFocus={() => setActive(item.id)}
                  onMouseEnter={() => setActive(item.id)}
                  role="menuitem"
                  type="button"
                >
                  <Icon aria-hidden className="size-3.5 text-muted-foreground" />
                  {item.label}
                </button>
              );
            })}
          </div>
          <div className="min-w-64 p-3">
            {active === "dates" ? (
              <DateRangePicker
                end={filters.end}
                onChange={(start, end) => props.onChange({ ...filters, end, start })}
                start={filters.start}
              />
            ) : null}
            {active === "duration" ? (
              <div className="grid gap-2">
                <label className="grid gap-1 text-xs text-muted-foreground">
                  Minimum minutes
                  <input
                    className={dashboardControlClassName}
                    min={0}
                    onChange={(event) =>
                      props.onChange({ ...filters, durationMinMinutes: event.target.value })
                    }
                    type="number"
                    value={filters.durationMinMinutes ?? ""}
                  />
                </label>
                <label className="grid gap-1 text-xs text-muted-foreground">
                  Maximum minutes
                  <input
                    className={dashboardControlClassName}
                    min={0}
                    onChange={(event) =>
                      props.onChange({ ...filters, durationMaxMinutes: event.target.value })
                    }
                    type="number"
                    value={filters.durationMaxMinutes ?? ""}
                  />
                </label>
              </div>
            ) : null}
            {active === "navigator" ? (
              <SuggestionList
                end={filters.end}
                field="navigator"
                label="navigators"
                onToggle={(value) =>
                  props.onChange({ ...filters, navigators: toggleValue(filters.navigators, value) })
                }
                selected={filters.navigators}
                start={filters.start}
              />
            ) : null}
            {active === "carespace" ? (
              <SuggestionList
                acceptsTypedUuid
                end={filters.end}
                field="carespaceId"
                label="carespace ids"
                onToggle={(value) =>
                  props.onChange({
                    ...filters,
                    carespaceIds: toggleValue(filters.carespaceIds, value),
                  })
                }
                selected={filters.carespaceIds}
                start={filters.start}
              />
            ) : null}
            {active === "handlerRole" ? (
              <ChoiceList
                onToggle={(value) => {
                  const role = HANDLER_ROLE_OPTIONS.find((item) => item.value === value);
                  if (!role) return;
                  props.onChange({
                    ...filters,
                    handlerRoles: toggleValue(filters.handlerRoles, role.value),
                  });
                }}
                options={HANDLER_ROLE_OPTIONS.map((role) => ({
                  label: role.label,
                  value: role.value,
                }))}
                selected={filters.handlerRoles}
              />
            ) : null}
            {active === "clinic" ? (
              <SuggestionList
                end={filters.end}
                field="clinic"
                label="clinics"
                onToggle={(value) =>
                  props.onChange({ ...filters, clinics: toggleValue(filters.clinics, value) })
                }
                selected={filters.clinics}
                start={filters.start}
              />
            ) : null}
            {active === "caregiver" ? (
              <SuggestionList
                end={filters.end}
                field="caregiver"
                label="people"
                onToggle={(value) =>
                  props.onChange({
                    ...filters,
                    caregivers: toggleValue(filters.caregivers, value),
                  })
                }
                selected={filters.caregivers}
                start={filters.start}
              />
            ) : null}
            {active === "externalNumber" ? (
              <ExternalNumberFilter
                onToggle={(value) =>
                  props.onChange({
                    ...filters,
                    externalNumbers: toggleValue(filters.externalNumbers, value),
                  })
                }
                selected={filters.externalNumbers}
              />
            ) : null}
            {active === "callType" ? (
              <SuggestionList
                end={filters.end}
                field="callType"
                label="call types"
                onToggle={(value) =>
                  props.onChange({ ...filters, callTypes: toggleValue(filters.callTypes, value) })
                }
                selected={filters.callTypes}
                start={filters.start}
              />
            ) : null}
            {active === "source" ? (
              <ChoiceList
                onToggle={(value) =>
                  props.onChange({
                    ...filters,
                    sources: toggleValue(filters.sources, value === "daily" ? "daily" : "dialpad"),
                  })
                }
                options={[
                  { label: "Dialpad", value: "dialpad" },
                  { label: "Daily", value: "daily" },
                ]}
                selected={filters.sources}
              />
            ) : null}
            {active === "callDirection" ? (
              <ChoiceList
                onToggle={(value) =>
                  props.onChange({
                    ...filters,
                    callDirections: toggleValue(
                      filters.callDirections,
                      value === "inbound" ? "inbound" : "outbound",
                    ),
                  })
                }
                options={[
                  { label: "Inbound", value: "inbound" },
                  { label: "Outbound", value: "outbound" },
                ]}
                selected={filters.callDirections}
              />
            ) : null}
            {active === "transcript" ? (
              <ChoiceList
                onToggle={(value) =>
                  props.onChange({
                    ...filters,
                    transcripts: toggleValue(
                      filters.transcripts,
                      value === "true" ? "true" : "false",
                    ),
                  })
                }
                options={[
                  { label: "Has transcript", value: "true" },
                  { label: "No transcript", value: "false" },
                ]}
                selected={filters.transcripts}
              />
            ) : null}
            {active === "recordingType" ? (
              <ChoiceList
                onToggle={(value) =>
                  props.onChange({
                    ...filters,
                    recordingTypes: toggleValue(
                      filters.recordingTypes,
                      value === "admincallrecording" ? "admincallrecording" : "callrecording",
                    ),
                  })
                }
                options={[
                  { label: "Call recording", value: "callrecording" },
                  { label: "Admin recording", value: "admincallrecording" },
                ]}
                selected={filters.recordingTypes}
              />
            ) : null}
          </div>
        </div>
      </PopoverPopup>
    </Popover>
  );
}

function FilterChip(props: { children: ReactNode; onClear: () => void }) {
  return (
    <button className={buttonClass} onClick={props.onClear} type="button">
      {props.children}
      <XIcon aria-hidden className="size-3" />
      <span className="sr-only">Clear filter</span>
    </button>
  );
}

function CallAudioDownloadDialog(props: {
  filters: CallAudioRecordingFilters;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) {
  const [includeAudio, setIncludeAudio] = useState(true);
  const [includeTranscripts, setIncludeTranscripts] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState<CallAudioDownloadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!props.open) return;
    setIncludeAudio(true);
    setIncludeTranscripts(true);
    setProgress(null);
    setError(null);
  }, [props.open]);
  const preview = useQuery(
    callAudioDownloadPreviewQueryOptions(
      props.filters,
      { audio: includeAudio, transcripts: includeTranscripts },
      props.open,
    ),
  );
  return (
    <Dialog onOpenChange={props.onOpenChange} open={props.open}>
      <DialogPopup className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Download</DialogTitle>
          <DialogDescription>
            Saves the matching recordings into a folder you choose. Uncheck audio to save
            transcripts only.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel>
          <div className="grid gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                checked={includeAudio}
                onChange={(event) => setIncludeAudio(event.target.checked)}
                type="checkbox"
              />
              Audio files
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                checked={includeTranscripts}
                onChange={(event) => setIncludeTranscripts(event.target.checked)}
                type="checkbox"
              />
              Transcripts
            </label>
            <ul className="grid gap-1 text-sm text-muted-foreground">
              {(preview.data?.fields ?? []).map((field) => (
                <li key={field}>{field}</li>
              ))}
            </ul>
            {preview.isError ? (
              <div className="grid gap-2">
                <p className="text-sm text-red-700">
                  {preview.error instanceof Error
                    ? preview.error.message
                    : "Could not prepare this download."}
                </p>
                <button
                  className={`${buttonClass} w-fit`}
                  onClick={() => void preview.refetch()}
                  type="button"
                >
                  <RefreshCwIcon aria-hidden className="size-3.5" />
                  Retry
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm">
                  {preview.data
                    ? `${preview.data.matchedRows.toLocaleString()} recordings match.`
                    : "Checking how many recordings match…"}
                </p>
                <p className="text-sm">
                  {includeAudio
                    ? "Audio is read directly from storage and saved as each file arrives."
                    : "Transcripts and the manifest only. Audio is not included."}
                </p>
                {includeAudio && preview.data?.matchedBytes != null ? (
                  <p className="text-sm">
                    This set is {formatDownloadBytes(preview.data.matchedBytes)} of audio.
                  </p>
                ) : null}
              </>
            )}
            {progress ? (
              <p className="text-sm" role="status">
                Saved {progress.completed.toLocaleString()} of {progress.total.toLocaleString()}
                {progress.failed > 0 ? `. ${progress.failed.toLocaleString()} failed.` : "."}
              </p>
            ) : null}
            {error ? <p className="text-sm text-red-700">{error}</p> : null}
          </div>
        </DialogPanel>
        <div className="flex justify-end px-6 pb-6">
          <button
            className={buttonClass}
            disabled={downloading || preview.isLoading || preview.isError}
            onClick={() => {
              setError(null);
              void (async () => {
                let directory: FileSystemDirectoryHandle;
                try {
                  directory = await chooseCallAudioDownloadDirectory();
                } catch (pickerError: unknown) {
                  if (callAudioDownloadPickerCancelled(pickerError)) return;
                  setError(
                    pickerError instanceof Error
                      ? pickerError.message
                      : "Choose a folder to save this download.",
                  );
                  return;
                }
                setDownloading(true);
                setProgress(null);
                try {
                  const manifest = await fetchCallAudioDownloadManifest(props.filters, {
                    audio: includeAudio,
                    transcripts: includeTranscripts,
                  });
                  const result = await writeCallAudioDownload({
                    directory,
                    manifest,
                    onProgress: setProgress,
                  });
                  if (result.failed > 0) {
                    setError(
                      `${result.failed.toLocaleString()} files could not be saved. The rest are in the folder.`,
                    );
                  }
                } catch (downloadError: unknown) {
                  setError(
                    downloadError instanceof Error
                      ? downloadError.message
                      : "Failed to download call audio",
                  );
                } finally {
                  setDownloading(false);
                }
              })();
            }}
            type="button"
          >
            <DownloadIcon aria-hidden className="size-3.5" />
            {progress
              ? `Saving ${progress.completed} of ${progress.total}`
              : downloading
                ? "Preparing…"
                : "Download"}
          </button>
        </div>
      </DialogPopup>
    </Dialog>
  );
}

function formatDownloadBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function TranscriptDialog(props: {
  onOpenChange: (open: boolean) => void;
  row: CallAudioRecording | null;
  timezone: string;
}) {
  const row = props.row;
  return (
    <Dialog onOpenChange={props.onOpenChange} open={row != null}>
      <DialogPopup className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Transcript</DialogTitle>
          <DialogDescription>
            {row
              ? `${formatWhen(row.occurredAt, props.timezone)} · ${row.navigatorName ?? row.navigatorEmail ?? "No navigator"} · ${row.carespaceId ?? "No carespace"} · ${formatDuration(row.durationSeconds)} · ${row.callType || "No call type"}`
              : "Call transcript"}
          </DialogDescription>
        </DialogHeader>
        {row ? <TranscriptBody row={row} /> : null}
      </DialogPopup>
    </Dialog>
  );
}

function TranscriptBody(props: { row: CallAudioRecording }) {
  const { row } = props;
  const transcript = useQuery(callAudioTranscriptQueryOptions(row.source, row.recordingId, true));
  const text = transcript.data?.text;
  return (
    <div className="grid gap-3 px-6 pb-6">
      <div className="flex justify-end">
        <button
          className={buttonClass}
          disabled={!text}
          onClick={() => {
            if (!text) return;
            const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${row.recordingId.replace(/[^A-Za-z0-9._-]/g, "_")}.txt`;
            link.click();
            URL.revokeObjectURL(url);
          }}
          type="button"
        >
          <DownloadIcon aria-hidden className="size-3.5" />
          Download
        </button>
      </div>
      {transcript.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading transcript…</p>
      ) : null}
      {transcript.error ? (
        <p className="text-sm text-red-700">
          {transcript.error instanceof Error
            ? transcript.error.message
            : "Failed to load the transcript"}
        </p>
      ) : null}
      {text ? (
        <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-md border border-border/70 bg-background p-3 text-sm text-foreground">
          {text}
        </pre>
      ) : null}
    </div>
  );
}

export function CallAudioDashboard() {
  const [filters, setFilters] = useState(defaultFilters);
  const [pageRequest, setPageRequest] = useState<CallAudioPageRequest>({
    cursor: null,
    pageDirection: "first",
  });
  const [transcriptRow, setTranscriptRow] = useState<CallAudioRecording | null>(null);
  const freshGeneration = useRef(0);
  const activeFresh = useRef(0);
  const defaults = defaultFilters();
  const datesAreDefault = filters.start === defaults.start && filters.end === defaults.end;
  const error = rangeError(filters.start, filters.end);
  const durationError = durationRangeError(filters);
  const takeFresh = () =>
    activeFresh.current !== 0 && activeFresh.current === freshGeneration.current;
  const query = useQuery({
    ...callAudioRecordingsQueryOptions(filters, pageRequest, takeFresh),
    enabled: error == null && durationError == null,
  });
  const count = useQuery({
    ...callAudioRecordingsCountQueryOptions(filters, takeFresh),
    enabled: error == null && durationError == null,
  });
  const data = query.data;
  const replaceFilters = (next: CallAudioRecordingFilters) => {
    setFilters(next);
    setPageRequest({ cursor: null, pageDirection: "first" });
  };
  const clearAll = () => replaceFilters(defaultFilters());
  const goToPage = (pageDirection: CallAudioPageRequest["pageDirection"]) => {
    if (pageDirection === "first" || pageDirection === "last") {
      setPageRequest({ cursor: null, pageDirection });
      return;
    }
    const cursor = pageDirection === "next" ? data?.nextCursor : data?.prevCursor;
    if (!cursor) return;
    setPageRequest({ cursor, pageDirection });
  };

  return (
    <DashboardsPageFrame className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            Care Navigation
          </p>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Call Audio</h1>
          <p className="mt-1 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Archived Dialpad and Daily recordings. The table opens on the last 7 days. Clear the
            dates to search the whole archive. Play asks for a five-minute signed URL when you press
            it. The list itself never includes one.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 text-right">
          <button
            className={buttonClass}
            disabled={query.isFetching || count.isFetching || error != null}
            onClick={() => {
              freshGeneration.current += 1;
              activeFresh.current = freshGeneration.current;
              const generation = freshGeneration.current;
              void Promise.allSettled([query.refetch(), count.refetch()]).then(() => {
                if (activeFresh.current === generation) activeFresh.current = 0;
              });
            }}
            type="button"
          >
            <RefreshCwIcon
              aria-hidden
              className={query.isFetching ? "size-3.5 animate-spin" : "size-3.5"}
            />
            {query.isFetching || count.isFetching ? "Refreshing…" : "Refresh"}
          </button>
          <p className="text-xs text-muted-foreground">
            {data
              ? `Refreshed ${formatWhen(data.generatedAt, data.timezone)}`
              : "Live recording query"}
          </p>
        </div>
      </header>
      {data?.dialpadStatus === "unavailable" ? (
        <p
          className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          role="status"
        >
          Dialpad recordings are not on the analytics database yet. Daily recordings are listed.
        </p>
      ) : null}
      {query.error ? (
        <div
          className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          role="alert"
        >
          {query.error instanceof Error
            ? query.error.message
            : "Failed to load call audio recordings"}
        </div>
      ) : null}
      {error ? (
        <p className="text-sm text-red-700" role="alert">
          {error}
        </p>
      ) : null}
      {durationError ? (
        <p className="text-sm text-red-700" role="alert">
          {durationError}
        </p>
      ) : null}
      <Recordings
        data={data}
        filters={filters}
        loading={query.isFetching || count.isFetching}
        onClearAll={clearAll}
        onFilters={replaceFilters}
        onOpenTranscript={setTranscriptRow}
        onPage={goToPage}
        onPageSize={(pageSize) => replaceFilters({ ...filters, pageSize })}
        pagingDisabled={query.isPlaceholderData}
        showClear={!datesAreDefault || hasActiveFilters(filters)}
        total={count.data?.total ?? null}
      />
      <TranscriptDialog
        onOpenChange={(open) => {
          if (!open) setTranscriptRow(null);
        }}
        row={transcriptRow}
        timezone={data?.timezone ?? "America/New_York"}
      />
    </DashboardsPageFrame>
  );
}

function hasActiveFilters(filters: CallAudioRecordingFilters): boolean {
  return (
    filters.navigators.length > 0 ||
    filters.handlerRoles.length > 0 ||
    filters.carespaceIds.length > 0 ||
    filters.clinics.length > 0 ||
    filters.caregivers.length > 0 ||
    filters.externalNumbers.length > 0 ||
    filters.callTypes.length > 0 ||
    filters.sources.length > 0 ||
    filters.callDirections.length > 0 ||
    filters.recordingTypes.length > 0 ||
    filters.transcripts.length > 0 ||
    Boolean(filters.durationMinMinutes?.trim()) ||
    Boolean(filters.durationMaxMinutes?.trim()) ||
    filters.sort != null
  );
}

function Recordings(props: {
  data: CallAudioRecordingsResponse | undefined;
  filters: CallAudioRecordingFilters;
  loading: boolean;
  onClearAll: () => void;
  onFilters: (filters: CallAudioRecordingFilters) => void;
  onOpenTranscript: (row: CallAudioRecording) => void;
  onPage: (pageDirection: CallAudioPageRequest["pageDirection"]) => void;
  onPageSize: (pageSize: CallAudioPageSize) => void;
  pagingDisabled: boolean;
  showClear: boolean;
  total: number | null;
}) {
  const { data, filters } = props;
  const [downloadOpen, setDownloadOpen] = useState(false);
  const chips: Array<{ clear: () => void; label: string }> = [
    ...(filters.start === defaultFilters().start && filters.end === defaultFilters().end
      ? []
      : [
          {
            clear: () => {
              const defaults = defaultFilters();
              props.onFilters({ ...filters, end: defaults.end, start: defaults.start });
            },
            label:
              filters.start && filters.end ? `${filters.start} to ${filters.end}` : "All dates",
          },
        ]),
    ...valueChips("Duration", durationLabel(filters), () =>
      props.onFilters({ ...filters, durationMaxMinutes: "", durationMinMinutes: "" }),
    ),
    ...filters.navigators.map((value) => ({
      clear: () =>
        props.onFilters({
          ...filters,
          navigators: filters.navigators.filter((item) => item !== value),
        }),
      label: value,
    })),
    ...filters.handlerRoles.map((value) => ({
      clear: () =>
        props.onFilters({
          ...filters,
          handlerRoles: filters.handlerRoles.filter((item) => item !== value),
        }),
      label: HANDLER_ROLE_OPTIONS.find((role) => role.value === value)?.label ?? value,
    })),
    ...filters.clinics.map((value) => ({
      clear: () =>
        props.onFilters({ ...filters, clinics: filters.clinics.filter((item) => item !== value) }),
      label: value,
    })),
    ...filters.caregivers.map((value) => ({
      clear: () =>
        props.onFilters({
          ...filters,
          caregivers: filters.caregivers.filter((item) => item !== value),
        }),
      label: value,
    })),
    ...filters.externalNumbers.map((value) => ({
      clear: () =>
        props.onFilters({
          ...filters,
          externalNumbers: filters.externalNumbers.filter((item) => item !== value),
        }),
      label: value,
    })),
    ...filters.carespaceIds.map((value) => ({
      clear: () =>
        props.onFilters({
          ...filters,
          carespaceIds: filters.carespaceIds.filter((item) => item !== value),
        }),
      label: value,
    })),
    ...filters.callTypes.map((value) => ({
      clear: () =>
        props.onFilters({
          ...filters,
          callTypes: filters.callTypes.filter((item) => item !== value),
        }),
      label: value,
    })),
    ...filters.sources.map((value) => ({
      clear: () =>
        props.onFilters({ ...filters, sources: filters.sources.filter((item) => item !== value) }),
      label: value === "dialpad" ? "Dialpad" : "Daily",
    })),
    ...filters.callDirections.map((value) => ({
      clear: () =>
        props.onFilters({
          ...filters,
          callDirections: filters.callDirections.filter((item) => item !== value),
        }),
      label: value,
    })),
    ...filters.transcripts.map((value) => ({
      clear: () =>
        props.onFilters({
          ...filters,
          transcripts: filters.transcripts.filter((item) => item !== value),
        }),
      label: value === "true" ? "Has transcript" : "No transcript",
    })),
    ...filters.recordingTypes.map((value) => ({
      clear: () =>
        props.onFilters({
          ...filters,
          recordingTypes: filters.recordingTypes.filter((item) => item !== value),
        }),
      label: value === "callrecording" ? "Call recording" : "Admin recording",
    })),
  ];
  return (
    <>
      <KpiGrid className="lg:grid-cols-2">
        <KpiCard
          label="Archived recordings"
          value={props.total == null ? "…" : props.total.toLocaleString()}
        />
        <KpiCard label="Showing" value={(data?.rows.length ?? 0).toLocaleString()} />
      </KpiGrid>
      <CallAudioDownloadDialog
        filters={filters}
        onOpenChange={setDownloadOpen}
        open={downloadOpen}
      />
      <section
        aria-busy={props.loading}
        className="relative overflow-hidden rounded-lg border border-border/70 bg-background"
      >
        <div inert={props.loading ? true : undefined}>
          <div className="flex flex-wrap items-center gap-2 border-b border-border/70 px-3 py-2">
            <FilterMenu filters={filters} onChange={props.onFilters} />
            {chips.map((chip) => (
              <FilterChip key={chip.label} onClear={chip.clear}>
                {chip.label}
              </FilterChip>
            ))}
            {props.showClear ? (
              <button className={buttonClass} onClick={props.onClearAll} type="button">
                Clear all
              </button>
            ) : null}
            <button
              className={`${buttonClass} ml-auto`}
              onClick={() => setDownloadOpen(true)}
              type="button"
            >
              <DownloadIcon aria-hidden className="size-3.5" />
              Download
            </button>
          </div>
          <DataTable
            columns={[
              {
                key: "occurredAt",
                label: "When",
                render: (row) => (
                  <span className="whitespace-nowrap">
                    {formatWhen(row.occurredAt, data?.timezone ?? "America/New_York")}
                  </span>
                ),
                sortable: true,
              },
              {
                key: "source",
                label: "Source",
                render: (row) => (row.source === "dialpad" ? "Dialpad" : "Daily"),
                sortable: true,
              },
              {
                key: "navigatorName",
                label: "Handler",
                render: (row) => (
                  <DetailPopover
                    label={row.navigatorName ?? row.navigatorEmail ?? "—"}
                    lines={[
                      ["Role", row.handlerRole],
                      ["Email", row.navigatorEmail],
                      ["Carespaces user", row.handlerUserId],
                      ["Dialpad target", row.handlerDialpadTargetId],
                      ["Enrollment list", row.handlerEnrollmentList ? "Yes" : "No"],
                    ]}
                  />
                ),
                sortable: true,
              },
              {
                key: "externalNumber",
                label: "External number",
                render: (row) => (
                  <DetailPopover
                    label={row.externalNumber ?? "—"}
                    lines={[
                      ["Internal number", row.internalNumber],
                      ["Contact phone", row.contactPhone],
                      ["Selected caller id", row.selectedCallerId],
                    ]}
                  />
                ),
              },
              {
                key: "carespace",
                label: "Carespace",
                render: (row) => <CarespaceCell row={row} />,
              },
              {
                key: "clinic",
                label: "Clinic",
                render: (row) => row.clinicName ?? "—",
              },
              {
                key: "people",
                label: "People",
                render: (row) => <PeopleCell row={row} />,
              },
              {
                key: "callType",
                label: "Type",
                render: (row) => row.callType || "—",
                sortable: true,
              },
              {
                key: "direction",
                label: "Direction",
                render: (row) => row.direction ?? "—",
                sortable: true,
              },
              {
                align: "right",
                key: "durationSeconds",
                label: "Duration",
                render: (row) => formatDuration(row.durationSeconds),
                sortable: true,
              },
              {
                key: "hasTranscript",
                label: "Transcript",
                render: (row) => (row.hasTranscript ? "Yes" : "No"),
              },
              {
                key: "viewTranscript",
                label: "View Transcript",
                render: (row) =>
                  row.readableTranscript ? (
                    <button
                      className={buttonClass}
                      onClick={() => props.onOpenTranscript(row)}
                      type="button"
                    >
                      Read Transcript
                    </button>
                  ) : (
                    "—"
                  ),
              },
              {
                key: "play",
                label: "Audio",
                render: (row) => <RecordingPlayer row={row} />,
              },
            ]}
            emptyMessage="No archived recordings in this range."
            rowKey={(row) => `${row.source}:${row.recordingId}`}
            rows={data ? [...data.rows] : []}
            serverSort={{
              ...(filters.sort ? { column: filters.sort } : {}),
              ...(filters.sortDirection ? { direction: filters.sortDirection } : {}),
              onCycle: (column) => {
                if (!isSortColumn(column)) return;
                props.onFilters(cycleCallAudioSort(filters, column));
              },
            }}
          />
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border/70 px-3 py-2">
            <span className="text-xs text-muted-foreground">
              {props.total == null ? "…" : props.total.toLocaleString()} recordings
            </span>
            <label className="flex items-center gap-1 text-xs text-muted-foreground">
              Rows
              <DashboardSelect
                onChange={(event) => {
                  const pageSize = Number(event.target.value);
                  if (
                    pageSize === CALL_AUDIO_RECORDINGS_PAGE_SIZES[0] ||
                    pageSize === CALL_AUDIO_RECORDINGS_PAGE_SIZES[1] ||
                    pageSize === CALL_AUDIO_RECORDINGS_PAGE_SIZES[2]
                  ) {
                    props.onPageSize(pageSize);
                  }
                }}
                value={String(filters.pageSize)}
              >
                {CALL_AUDIO_RECORDINGS_PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </DashboardSelect>
            </label>
            <button
              aria-label="First page"
              className={buttonClass}
              disabled={props.pagingDisabled || data?.prevCursor == null}
              onClick={() => props.onPage("first")}
              type="button"
            >
              <ChevronsLeftIcon aria-hidden className="size-3.5" />
            </button>
            <button
              aria-label="Previous page"
              className={buttonClass}
              disabled={props.pagingDisabled || data?.prevCursor == null}
              onClick={() => props.onPage("prev")}
              type="button"
            >
              <ChevronLeftIcon aria-hidden className="size-3.5" />
            </button>
            <button
              aria-label="Next page"
              className={buttonClass}
              disabled={props.pagingDisabled || data?.nextCursor == null}
              onClick={() => props.onPage("next")}
              type="button"
            >
              <ChevronRightIcon aria-hidden className="size-3.5" />
            </button>
            <button
              aria-label="Last page"
              className={buttonClass}
              disabled={props.pagingDisabled || data?.nextCursor == null}
              onClick={() => props.onPage("last")}
              type="button"
            >
              <ChevronsRightIcon aria-hidden className="size-3.5" />
            </button>
          </div>
        </div>
        {props.loading ? (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center gap-3 bg-background/80"
            role="status"
          >
            <RefreshCwIcon aria-hidden className="size-6 animate-spin" />
            <span className="text-sm font-medium text-foreground">Loading recordings…</span>
          </div>
        ) : null}
      </section>
    </>
  );
}

function DetailPopover(props: { label: string; lines: Array<[string, string | null]> }) {
  const lines = props.lines.filter(
    (line): line is [string, string] => line[1] != null && line[1] !== "",
  );
  return (
    <Popover>
      <PopoverTrigger className="text-left underline decoration-dotted underline-offset-2">
        {props.label}
      </PopoverTrigger>
      <PopoverPopup align="start" className="grid w-64 gap-1 p-3 text-xs">
        {lines.length === 0 ? (
          <p className="text-muted-foreground">No extra details</p>
        ) : (
          lines.map(([label, value]) => (
            <p key={label}>
              <span className="text-muted-foreground">{label}: </span>
              {value}
            </p>
          ))
        )}
      </PopoverPopup>
    </Popover>
  );
}

function CarespaceCell(props: { row: CallAudioRecording }) {
  const { row } = props;
  if (row.carespaceMatch === "matched" && row.carespaceId) {
    return (
      <a
        className="text-sky-700 underline underline-offset-2 dark:text-sky-400"
        href={`https://app.craniometrix.com/carespaces/${encodeURIComponent(row.carespaceId)}`}
        rel="noreferrer"
        target="_blank"
      >
        {row.carespaceName ?? row.carespaceId}
      </a>
    );
  }
  if (row.carespaceMatch === "multiple") return "Multiple";
  if (row.carespaceMatch === "conflict") return "Conflict";
  return "No carespace";
}

function PeopleCell(props: { row: CallAudioRecording }) {
  const { row } = props;
  const label =
    row.caregiverCount === 1 ? (row.caregivers[0]?.name ?? "1") : String(row.caregiverCount);
  const caregiverLines = row.caregivers.flatMap((person) => {
    const detail = [person.relationship, person.type, person.status, person.email, ...person.phones]
      .filter((item) => item)
      .join(" · ");
    return [[person.name || "Caregiver", detail || null] as [string, string | null]];
  });
  return (
    <DetailPopover
      label={label}
      lines={[
        ["Assigned navigator", row.assignedNavigatorName],
        ["Assigned email", row.assignedNavigatorEmail],
        ...caregiverLines,
      ]}
    />
  );
}

function durationRangeError(filters: CallAudioRecordingFilters): string | null {
  const min = filters.durationMinMinutes?.trim();
  const max = filters.durationMaxMinutes?.trim();
  if (!min || !max) return null;
  const minMinutes = Number(min);
  const maxMinutes = Number(max);
  if (!Number.isInteger(minMinutes) || !Number.isInteger(maxMinutes)) return null;
  if (minMinutes <= maxMinutes) return null;
  return "Minimum duration must be less than or equal to the maximum.";
}

function durationLabel(filters: CallAudioRecordingFilters): string | null {
  const min = filters.durationMinMinutes?.trim();
  const max = filters.durationMaxMinutes?.trim();
  if (min && max) return `${min}–${max} min`;
  if (min) return `≥ ${min} min`;
  if (max) return `≤ ${max} min`;
  return null;
}

function valueChips(
  _label: string,
  value: string | null,
  clear: () => void,
): Array<{ clear: () => void; label: string }> {
  return value ? [{ clear, label: value }] : [];
}
